import { EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { SearchQuery } from '../models/search-query.model';
import { FlightSchoolFinderFilter, FLIGHT_SCHOOL_CLASSIFICATION_OPTIONS, FLIGHT_SCHOOL_CERTIFICATION_OPTIONS, FLIGHT_SCHOOL_RATING_OPTIONS, FLIGHT_SCHOOL_ADVANCED_OPTIONS, FLIGHT_SCHOOL_SERVICES_OPTIONS, FLIGHT_SCHOOL_OTHER_OPTIONS } from '../../shared/models/flight-school-finder-filter.model';
import { FlightSchoolFinderFiltersService } from '../../shared/services/flight-school-finder-filters.service';
import { AnalyticsService } from '../../airport/service/google-analytics-4.service';
/**
 * Filter UI for the Flight School Finder, mirroring {@link FlyingClubFinderFilterListComponent}. Renders a
 * keyword field plus the Training Type / Certifications / Ratings / Advanced Training / Services facet
 * groups. Reads and writes the shared {@link FlightSchoolFinderFiltersService} state and emits
 * `filtersChange`.
 */
var FlightSchoolFinderFilterListComponent = /** @class */ (function () {
    function FlightSchoolFinderFilterListComponent(_flightSchoolFinderFiltersService, _analyticsService) {
        this._flightSchoolFinderFiltersService = _flightSchoolFinderFiltersService;
        this._analyticsService = _analyticsService;
        this.filtersChange = new EventEmitter();
        /**
         * The collapsible facet sections, in display order: the group key on FlightSchoolFinderFilter, the header
         * label, and the checkbox options. Training Type uses OR logic; the rest use AND (see filterFlightSchools).
         */
        this.sections = [
            { key: 'classification', title: 'Training Type', options: FLIGHT_SCHOOL_CLASSIFICATION_OPTIONS },
            { key: 'certifications', title: 'Certifications', options: FLIGHT_SCHOOL_CERTIFICATION_OPTIONS },
            { key: 'ratings', title: 'Ratings', options: FLIGHT_SCHOOL_RATING_OPTIONS },
            { key: 'advanced', title: 'Advanced Training', options: FLIGHT_SCHOOL_ADVANCED_OPTIONS },
            { key: 'services', title: 'Services', options: FLIGHT_SCHOOL_SERVICES_OPTIONS },
            { key: 'other', title: 'VA/College', options: FLIGHT_SCHOOL_OTHER_OPTIONS }
        ];
        /**
         * Per-section expand/collapse state. There are far more Flight School facets than fit on one screen, so
         * sections start collapsed (compact, no scrolling); any section that already has selected filters is
         * auto-expanded on first load so restored/active filters are visible. Each header shows a count badge.
         */
        this.expanded = {
            classification: false,
            certifications: false,
            ratings: false,
            advanced: false,
            services: false,
            other: false
        };
        this._expansionInitialized = false;
        /** Max number of selected-filter chips to render before collapsing the rest into a "+N more" marker. */
        this.maxChips = 5;
        /** The chips actually shown (first {@link maxChips} selected filters, in section + option order). */
        this.visibleChips = [];
        /** How many selected filters are hidden beyond {@link maxChips} (0 = none hidden). */
        this.overflowCount = 0;
        this.flightSchoolFinderFilter = new FlightSchoolFinderFilter();
    }
    Object.defineProperty(FlightSchoolFinderFilterListComponent.prototype, "nameTooShort", {
        /**
         * True when a school name has been entered but is too short to count toward a search. Mirrors the
         * `schoolKeyword.length >= 3` gate in {@link SearchQuery.validate}, so the hint shows exactly while the
         * entered name would be ignored.
         */
        get: function () {
            var trimmed = this.flightSchoolFinderFilter.name ? this.flightSchoolFinderFilter.name.trim() : '';
            return trimmed.length > 0 && trimmed.length < 3;
        },
        enumerable: true,
        configurable: true
    });
    FlightSchoolFinderFilterListComponent.prototype.ngOnInit = function () {
        var _this = this;
        this._subscription = this._flightSchoolFinderFiltersService.flightSchoolFinderFilter.subscribe(function (f) {
            _this.flightSchoolFinderFilter = f;
            _this._recomputeChips();
            // Auto-expand sections with active filters once (on first load); don't fight the user's manual
            // toggles on subsequent emissions (each checkbox edit re-emits the same filter).
            if (!_this._expansionInitialized) {
                _this._expansionInitialized = true;
                _this.sections.forEach(function (s) { return _this.expanded[s.key] = !!(f && f[s.key] && f[s.key].length); });
            }
        });
        this._analyticsService.pageView('airport_landingpage_flightschool', 'Flight School Finder Landing Page filters');
    };
    FlightSchoolFinderFilterListComponent.prototype.ngOnDestroy = function () {
        if (this._subscription) {
            this._subscription.unsubscribe();
        }
    };
    FlightSchoolFinderFilterListComponent.prototype.isFacetSelected = function (group, value) {
        return this.flightSchoolFinderFilter[group].indexOf(value) !== -1;
    };
    FlightSchoolFinderFilterListComponent.prototype.onToggleFacet = function (group, value, checked) {
        var selected = this.flightSchoolFinderFilter[group];
        var i = selected.indexOf(value);
        if (checked && i === -1) {
            selected.push(value);
        }
        else if (!checked && i !== -1) {
            selected.splice(i, 1);
        }
        this._emit();
    };
    FlightSchoolFinderFilterListComponent.prototype.onSelectAll = function (group, options) {
        this.flightSchoolFinderFilter[group] = options.map(function (o) { return o.value; });
        this._emit();
    };
    FlightSchoolFinderFilterListComponent.prototype.onClearAll = function (group) {
        this.flightSchoolFinderFilter[group] = [];
        this._emit();
    };
    FlightSchoolFinderFilterListComponent.prototype.onNameChange = function () {
        this._emit();
    };
    FlightSchoolFinderFilterListComponent.prototype.toggleSection = function (group) {
        this.expanded[group] = !this.expanded[group];
    };
    FlightSchoolFinderFilterListComponent.prototype.trackByChip = function (index, chip) {
        return chip.value;
    };
    /**
     * Rebuilds the selected-filter chip list from the current filter state (called on every filter change).
     * Chips are collected in section + option order; only the first {@link maxChips} are shown, the rest are
     * summarized by {@link overflowCount}.
     */
    FlightSchoolFinderFilterListComponent.prototype._recomputeChips = function () {
        var _this = this;
        var chips = [];
        this.sections.forEach(function (section) {
            var selected = _this.flightSchoolFinderFilter[section.key] || [];
            section.options.forEach(function (option) {
                if (selected.indexOf(option.value) !== -1) {
                    chips.push({ group: section.key, value: option.value, label: option.label });
                }
            });
        });
        this.visibleChips = chips.slice(0, this.maxChips);
        this.overflowCount = Math.max(0, chips.length - this.maxChips);
    };
    /** Number of selected filters in a section (shown as the header count badge). */
    FlightSchoolFinderFilterListComponent.prototype.selectedCount = function (group) {
        var selected = this.flightSchoolFinderFilter[group];
        return selected ? selected.length : 0;
    };
    FlightSchoolFinderFilterListComponent.prototype._emit = function () {
        // Keep the query's filter in sync as the user edits, so a school name entered here counts toward search
        // validation (see SearchQuery.validate) even before the query is serialized/submitted.
        if (this.query) {
            this.query.flightSchoolFinderFilter = this.flightSchoolFinderFilter;
        }
        this._flightSchoolFinderFiltersService.updateFlightSchoolFinderFilters(this.flightSchoolFinderFilter);
        this.filtersChange.emit();
    };
    return FlightSchoolFinderFilterListComponent;
}());
export { FlightSchoolFinderFilterListComponent };
